import * as React from 'react';
import { JRCIcon } from '../JRCIcon/JRCIcon';
import styled from 'styled-components';
import { DashedFocusBorder } from 'components/Common/DashedFocusBorder';
import { Colors, ColorsOrShades, Shades } from 'styles/theme';
import { JRCTooltipProps } from '../JRCTooltip/JRCTooltip';
import { JRCConditionalTooltip } from '../Common/JRCConditionalWrapper';
import { DataCy } from '../../types/dataAttributes';
import { useMemo } from 'react';

const Button = styled.button.attrs((props: { size: 'small' | 'medium' }) => ({
    ...props,
    offset: config[props.size].offset,
}))<JRCIconButtonProps>`
    display: inline-flex;
    justify-content: center;
    align-items: center;
    width: ${(props) => config[props.size].size};
    height: ${(props) => config[props.size].size};
    border-radius: ${(props) => config[props.size].borderRadius};
    cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
    flex-shrink: 0;
    transition: all 0.2s;
    ${(props) =>
        props.borderColor &&
        `
        border: 2px solid ${props.theme.color[props.borderColor]};
    `}
    ${(props) =>
        props.bg
            ? `background-color: ${props.theme.color[props.bg]}`
            : props.color !== 'white' &&
              props.bgBaseGrey &&
              props.bgBaseGrey > 0 &&
              `background-color: ${props.theme.color[`grey${props.bgBaseGrey - 1}` as ColorsOrShades]}`};

    :hover,
    :focus-visible {
        ${(props) =>
            !props.disabled &&
            `background-color: ${
                props.bgHover
                    ? props.theme.color[props.bgHover]
                    : props.color === 'white'
                    ? props.theme.color.black15
                    : props.theme.color[`grey${props.bgBaseGrey || 0}`]
            }`};
    }

    ${(props) => DashedFocusBorder({ offset: props.offset })}
`;

const Badge = styled.p<Pick<JRCIconButtonProps, 'size' | 'bg' | 'bgBaseGrey'> & { inline?: boolean }>`
    position: absolute;
    background-color: ${({ theme, inline }) => (inline ? 'none' : theme.color.orange)};
    color: ${({ theme, inline, bg, bgBaseGrey }) =>
        inline ? (bg ? theme.color[bg] : theme.color[`grey${bgBaseGrey ?? 0}`]) : theme.color.white};
    font-size: ${({ size, inline }) => (size === 'medium' ? (inline ? '10px' : '12px') : 0)};
    border-radius: 10px;
    margin-top: ${({ size, inline }) => (size === 'medium' ? (inline ? 0 : 30) : 22)}px;
    margin-left: ${({ size, inline }) => (size === 'medium' ? (inline ? 0 : 28) : 22)}px;
    min-width: ${({ size }) => (size === 'medium' ? 17 : 10)}px;
    text-align: center;
    height: ${({ size }) => (size === 'medium' ? 17 : 10)}px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 5px;
`;

const config = {
    small: {
        size: '24px',
        borderRadius: '4px',
        iconSize: 16,
        offset: '-2px',
    },
    medium: {
        size: '40px',
        borderRadius: '8px',
        iconSize: 20,
        offset: '-3px',
    },
};

const colorVariants: Record<
    string,
    {
        color: Colors | Shades;
        bg: Colors | Shades;
        bgHover: Colors | Shades;
        borderColor: Colors | Shades;
    }
> = {
    light: {
        color: 'primary',
        bg: 'white',
        bgHover: 'primaryL80',
        borderColor: 'primary',
    },
    primary: {
        color: 'white',
        bg: 'primary',
        bgHover: 'primaryL15',
        borderColor: 'primary',
    },
} as const;

/**
 * IconButton props
 * @member icon icon of the button
 * @member size size of the button, default value, set to medium
 * @member onClick on click callback
 * @member name name (for form only)
 * @member value value (for form only)
 * @member disabled disable the button
 * @member type type of the button
 * @member bgBaseGrey base grey color (hover, focus, disabled, default icon color will be compute from this base)
 * @member tooltip tooltip props
 * @member variant can be 'circle' | 'square';
 * @member hasLicense If false, the button will be disabled with a license tooltip. Default to true
 */
export type JRCIconButtonProps = Omit<React.ComponentPropsWithoutRef<'button'>, 'onClick'> &
    DataCy & {
        icon: string;
        size?: 'small' | 'medium';
        onClick?: (event?: React.MouseEvent<HTMLButtonElement>) => void;
        name?: string;
        value?: string;
        disabled?: boolean;
        type?: 'submit' | 'reset' | 'button';
        bgBaseGrey?: 0 | 1 | 2;
        color?: Colors | Shades | 'inherit';
        tooltip?: Omit<JRCTooltipProps, 'children'>;
        variant?: 'circle' | 'square';
        hasLicense?: boolean;
        bg?: Colors | Shades;
        bgHover?: Colors | Shades;
        borderColor?: Colors | Shades;
        iconSize?: number;
        badgeNumber?: number;
        colorVariant?: keyof typeof colorVariants;
        badgeInline?: boolean;
    };

const defaultProps = {
    type: 'button',
    variant: 'square',
    size: 'medium',
};

const JRCIconButton = ({ hasLicense = true, ...props }: JRCIconButtonProps & typeof defaultProps) => {
    const bgBaseGrey = props.bgBaseGrey || 0;
    const disabled = props.disabled || !hasLicense;

    const colors = useMemo(() => {
        const variant = props.colorVariant ? colorVariants[props.colorVariant] : undefined;
        return {
            color: variant?.color ?? props.color,
            iconColor: disabled ? `grey${bgBaseGrey + 1}` : variant?.color ?? props.color ?? `grey${bgBaseGrey + 3}`,
            bg: variant?.bg ?? props.bg,
            bgHover: variant?.bgHover ?? props.bgHover,
            borderColor: variant?.borderColor,
        };
    }, [bgBaseGrey, disabled, props.bg, props.bgHover, props.color, props.colorVariant]);

    return (
        <>
            <JRCConditionalTooltip condition={!!props.tooltip} hasLicense={hasLicense} tooltip={props.tooltip}>
                <Button
                    {...props}
                    disabled={disabled}
                    bgBaseGrey={bgBaseGrey}
                    color={colors.color}
                    bg={colors.bg}
                    bgHover={colors.bgHover}
                    borderColor={colors.borderColor}
                    data-cy={props.dataCy}
                    size={props.size}>
                    <JRCIcon
                        name={props.icon}
                        size={props.iconSize ?? config[props.size].iconSize}
                        color={colors.iconColor}
                    />
                    {props.badgeNumber !== undefined && props.badgeNumber > 0 && (
                        <Badge inline={props.badgeInline} size={props.size} bg={props.bg} bgBaseGrey={props.bgBaseGrey}>
                            {props.badgeNumber}
                        </Badge>
                    )}
                </Button>
            </JRCConditionalTooltip>
        </>
    );
};

JRCIconButton.defaultProps = defaultProps;
export { JRCIconButton };
